import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/services/api';
import { 
  User, 
  Tenant, 
  LoginRequest, 
  RegisterRequest, 
  TokenResponse 
} from '@/types';

interface SimpleRegisterRequest {
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  registerSimple: (data: SimpleRegisterRequest) => Promise<void>;
  googleAuth: (credential: string) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  refreshProfileSilently: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true });
          
          console.log('Attempting login with:', credentials.email);
          
          // デバッグ: JSONリクエストを使用
          const response = await fetch('/api/v1/auth/login-debug', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: credentials.email,
              password: credentials.password,
            }),
          });
          
          console.log('Login response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Login error response:', errorText);
            throw new Error(`ログインに失敗しました: ${response.status}`);
          }
          
          const tokenData: TokenResponse = await response.json();
          
          // トークンを保存
          localStorage.setItem('access_token', tokenData.access_token);
          localStorage.setItem('refresh_token', tokenData.refresh_token);
          
          // プロフィール情報を取得
          await get().fetchProfile();
          
          set({ isAuthenticated: true });
        } catch (error) {
          console.error('Login failed:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (data: RegisterRequest) => {
        try {
          set({ isLoading: true });
          
          const response = await api.post<TokenResponse>('/auth/register', data);
          
          // トークンを保存
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);
          
          // プロフィール情報を取得
          await get().fetchProfile();
          
          set({ isAuthenticated: true });
        } catch (error) {
          console.error('Registration failed:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      registerSimple: async (data: SimpleRegisterRequest) => {
        try {
          set({ isLoading: true });
          
          const response = await api.post<TokenResponse>('/auth/register-simple', data);
          
          // トークンを保存
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);
          
          // プロフィール情報を取得
          await get().fetchProfile();
          
          set({ isAuthenticated: true });
        } catch (error) {
          console.error('Simple registration failed:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      googleAuth: async (credential: string) => {
        try {
          set({ isLoading: true });
          
          const response = await api.post<TokenResponse>('/auth/google', {
            credential
          });
          
          // トークンを保存
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);
          
          // プロフィール情報を取得
          await get().fetchProfile();
          
          set({ isAuthenticated: true });
        } catch (error) {
          console.error('Google auth failed:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({ 
          user: null, 
          tenant: null, 
          isAuthenticated: false 
        });
      },

      fetchProfile: async () => {
        try {
          const [userResponse, tenantResponse] = await Promise.all([
            api.get<User>('/auth/me'),
            api.get<Tenant>('/tenants/profile')
          ]);
          
          set({ 
            user: userResponse, 
            tenant: tenantResponse,
            isAuthenticated: true 
          });
        } catch (error) {
          console.error('Failed to fetch profile:', error);
          get().logout();
          throw error;
        }
      },

      refreshProfileSilently: async () => {
        try {
          const [userResponse, tenantResponse] = await Promise.all([
            api.get<User>('/auth/me'),
            api.get<Tenant>('/tenants/profile')
          ]);

          set({
            user: userResponse,
            tenant: tenantResponse,
            isAuthenticated: true
          });
        } catch (error) {
          console.error('Silent profile refresh failed:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

// 認証フック
export const useAuth = () => {
  const {
    user,
    tenant,
    isAuthenticated,
    isLoading,
    login,
    register,
    registerSimple,
    googleAuth,
    logout,
    fetchProfile,
    refreshProfileSilently,
  } = useAuthStore();

  return {
    user,
    tenant,
    isAuthenticated,
    isLoading,
    login,
    register,
    registerSimple,
    googleAuth,
    logout,
    fetchProfile,
    refreshProfileSilently,
  };
};
